import { ApiQueryName } from "@/models/api"
import type { Organization } from "@/models/organization"
import { isUnavailableBillError } from "@/services/bill/use-bill"
import type { BillPeriod } from "@/services/core-api/types"
import { useQueryKey, useRenameQueryResult } from "@/services/utils"
import { type HttpError, httpClient, toHttpError } from "@/utils/api"
import { resolveQueryThrowOnError } from "@/utils/queryClient"
import { useQuery } from "@tanstack/react-query"

interface GetBillPeriodParams {
  billPeriodId: BillPeriod["id"]
  organizationId: Organization["id"]
}

// One period with its source-file download link and its charge lines, so the
// bill page needs no bill, line-items, or document request.
const getBillPeriod = async ({
  billPeriodId,
  organizationId,
}: GetBillPeriodParams): Promise<BillPeriod> => {
  try {
    const response = await httpClient.get<BillPeriod>(
      `/api/v1/organizations/${organizationId}/bill-periods/${billPeriodId}`,
      {
        params: { include_download_url: true, include_line_items: true },
      }
    )

    return response.data
  } catch (error: unknown) {
    throw toHttpError(error)
  }
}

interface UseBillPeriodOptions {
  billPeriodId: BillPeriod["id"] | undefined
  organizationId: Organization["id"] | undefined
}

export const useBillPeriod = ({
  billPeriodId,
  organizationId,
}: UseBillPeriodOptions) => {
  const queryKey = useQueryKey(
    ApiQueryName.BillPeriod,
    "getOne",
    billPeriodId === undefined ? null : String(billPeriodId),
    { organizationId }
  )

  const queryResult = useQuery<BillPeriod, HttpError>({
    enabled: Boolean(organizationId) && billPeriodId !== undefined,
    queryFn: () => {
      if (!organizationId || billPeriodId === undefined) {
        throw new Error("organizationId and billPeriodId are required")
      }
      return getBillPeriod({ billPeriodId, organizationId })
    },
    queryKey,
    throwOnError: resolveQueryThrowOnError(
      (error) => !isUnavailableBillError(error)
    ),
  })

  return useRenameQueryResult(queryResult, ApiQueryName.BillPeriod)
}
